import { useMemo } from 'react';
import { Panel, tokens } from '@tiw/ui';
import { useStreamStore, type StreamEvent } from '@/stores/useStreamStore';

/**
 * Event log — bottom-right HUD feed of recent discrete events.
 *
 * Reads the stream store (incidents, topology updates, bgp, dns — the 2 Hz flow
 * tick is deliberately excluded upstream in useRealtimeStream) and renders the
 * most recent events newest-first, colour-coded by severity. See
 * docs/EVENT_ARCHITECTURE.md.
 */
const MAX_ROWS = 8;

type Tone = 'neutral' | 'good' | 'warn' | 'critical';

const TONE_COLOR: Record<Tone, string> = {
  neutral: tokens.color.text.primary,
  good: tokens.color.semantic.good,
  warn: tokens.color.semantic.warn,
  critical: tokens.color.semantic.critical,
};

function fmtTime(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function summarize(e: StreamEvent): { tag: string; label: string; tone: Tone } {
  const tag = e.channel.replace(/^tiw\./, '');
  const p = (e.payload ?? {}) as Record<string, unknown>;
  switch (e.channel) {
    case 'tiw.incident.open': {
      const scope = p.scope as { pop_ids?: readonly string[] } | undefined;
      const target = scope?.pop_ids?.[0];
      return {
        tag,
        label: `${String(p.title ?? 'incident')}${target ? ` · ${target}` : ''}`,
        tone: 'critical',
      };
    }
    case 'tiw.incident.close':
      return { tag, label: `resolved · ${String(p.title ?? p.incident_id ?? '')}`, tone: 'good' };
    case 'tiw.topology.update': {
      const changed = p.changed_links as readonly unknown[] | undefined;
      const n = changed?.length ?? 0;
      return { tag, label: `${n} link${n === 1 ? '' : 's'} updated`, tone: 'neutral' };
    }
    case 'tiw.bgp.withdraw':
      return { tag, label: `withdraw ${String(p.prefix ?? '')}`, tone: 'warn' };
    case 'tiw.dns.spike':
      return { tag, label: `spike ${String(p.resolver ?? '')}`, tone: 'warn' };
    default:
      return { tag, label: tag, tone: 'neutral' };
  }
}

export function EventLog(): JSX.Element {
  const byChannel = useStreamStore((s) => s.byChannel);
  const recent = useMemo(() => {
    const all = Object.values(byChannel).flat();
    all.sort((a, b) => b.ts - a.ts);
    return all.slice(0, MAX_ROWS);
  }, [byChannel]);

  return (
    <Panel
      title="EVENT LOG"
      style={{ position: 'absolute', bottom: 56, right: 24, width: 420, pointerEvents: 'auto' }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 7,
          fontFamily: tokens.type.family.mono,
          fontSize: tokens.type.size.sm,
          lineHeight: 1.35,
        }}
      >
        {recent.length === 0 ? (
          <span style={{ color: tokens.color.text.faint }}>awaiting events…</span>
        ) : (
          recent.map((e) => {
            // The colour already conveys open (red) vs resolved (green), so the
            // channel tag is dropped — the message gets the full width and wraps
            // instead of clipping.
            const { label, tone } = summarize(e);
            return (
              <div key={e.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ color: tokens.color.text.faint, flex: '0 0 auto' }}>{fmtTime(e.ts)}</span>
                <span style={{ color: TONE_COLOR[tone], flex: '1 1 auto', wordBreak: 'break-word' }}>
                  {label}
                </span>
              </div>
            );
          })
        )}
      </div>
    </Panel>
  );
}
